import { useCallback, useState } from "react";

import { send } from "../api/auth";
import { console_, type Requirement } from "../api/console";
import { StatusChip } from "../design/StatusChip";
import { translateLabel } from "../i18n/translate";
import { useT } from "../i18n/LocaleProvider";
import { useSession } from "../session/SessionProvider";
import { clock, whenIn } from "../system/format";
import { SearchSelect } from "./SearchSelect";

// ---------------------------------------------------------------------------
// SWEEP S11. PUT A NAMED PERSON ON A POST.
//
// The only way to offer work was match -> plan waves -> fire, which is right for filling
// forty posts from a bench and wrong for "Anna is doing the north stand again".
// `POST /v1/assignments` existed with no caller. The post's role and shift are the
// post's own; the server's gates — credentials at the shift's END (V8), conflicts (V15),
// working time (V31) — decide, and their sentence is shown as it comes.
// ---------------------------------------------------------------------------

export function BookSomeone({
  eventId,
  requirements,
  onBooked,
  open,
  onClose,
}: {
  eventId: string;
  requirements: Requirement[];
  onBooked: () => Promise<void>;
  /** Tour T-53: the form opens from the plus in the board's heading and closes again. */
  open: boolean;
  onClose: () => void;
}) {
  const { apiBase, me } = useSession();
  const { locale, t, refusal } = useT();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const [postId, setPostId] = useState("");
  const [workerId, setWorkerId] = useState("");
  const [contracted, setContracted] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: "live" | "failed"; text: string } | null>(null);

  const searchWorkers = useCallback(
    async (q: string) => {
      const res = await send<{ workers?: { id: string; display_name: string }[] }>(
        console_.listWorkers(apiBase, { q, limit: 20 }),
      );
      return (res.body.workers ?? []).map((w) => ({ id: w.id, label: w.display_name }));
    },
    [apiBase],
  );

  async function book() {
    const post = requirements.find((r) => r.id === postId);
    if (!post || !workerId) return;
    setBusy(true);
    setMessage(null);
    try {
      const res = await send<{ status?: string; error?: { message?: string } }>(
        console_.createAssignment(apiBase, {
          worker_id: workerId,
          role_def_id: post.role_def_id,
          starts_at: post.starts_at,
          ends_at: post.ends_at,
          requirement_id: post.id,
          event_id: eventId,
          idempotency_key: crypto.randomUUID(),
          ...(contracted ? { contracted_hours: Number(contracted) } : {}),
        }),
      );
      if (res.status !== 201) {
        setMessage({ tone: "failed", text: refusal(res.body.error) ?? t("That booking was refused.") });
        return;
      }
      setMessage({ tone: "live", text: t("Offered. They see it in their app and can accept or decline.") });
      setWorkerId("");
      await onBooked();
    } catch {
      setMessage({ tone: "failed", text: t("We could not reach Verbunk. Check your connection and try again.") });
    } finally {
      setBusy(false);
    }
  }

  if (requirements.length === 0 || !open) return null;

  return (
    <section className="panel stack">
      <div className="panel-head">
        <h2>{t("Offer a post to someone")}</h2>
      </div>
      <div className="form-grid">
        <div className="field">
          <label htmlFor="book-post">{t("Post")}</label>
          <select id="book-post" value={postId} onChange={(e) => setPostId(e.target.value)}>
            <option value="">{t("Choose a post…")}</option>
            {requirements.map((r) => (
              <option key={r.id} value={r.id}>
                {r.zone ?? translateLabel(locale, r.role_name)} — {translateLabel(locale, r.role_name)}, {whenIn(r.starts_at, orgZone)}–{clock(r.ends_at)}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="book-contracted">{t("Contracted hours (optional)")}</label>
          <input id="book-contracted" type="number" min="0.5" max="24" step="0.25" value={contracted} onChange={(e) => setContracted(e.target.value)} />
          <p className="field-hint">{t("Time worked beyond this is overtime. Left empty, overtime cannot be told on this booking.")}</p>
        </div>
        <SearchSelect
          id="book-worker"
          label={t("Person")}
          value={workerId}
          onChange={setWorkerId}
          search={searchWorkers}
          placeholder={t("Type a name")}
        />
      </div>
      {message ? (
        <div className="form-message" role="status" aria-live="polite">
          <StatusChip tone={message.tone} label={message.tone === "live" ? t("Offered") : t("Not offered")} />
          <span>{message.text}</span>
        </div>
      ) : null}
      <div className="row">
        <button type="button" className="btn" disabled={busy || !postId || !workerId} onClick={() => void book()}>
          {busy ? t("Offering…") : t("Offer the post")}
        </button>
        <button type="button" className="btn btn-quiet" onClick={onClose}>
          {t("Cancel")}
        </button>
      </div>
    </section>
  );
}
